import { Stack, useTheme, Typography, Link } from "@mui/material";
import ActivityDetailsText from "@/modules/lead-page/new/components/activity/activity-details-typography";
import UserNameTag from "@/modules/lead-page/new/components/activity/user-name-tag";
import { ArrowRight } from "lucide-react";
import Status from "@/modules/common/components/ui-kit/status/status";
import {
  FollowUpStatusEnum,
  FollowUpStatusColors,
} from "@/modules/lead-page/enums";
import { capitalizeFirstLetter } from "@/modules/common/utils";
import { Box } from "@mui/material";
import StarRating from "@/modules/common/components/star-rating";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import { formatDateReadable } from "@/modules/leads/utils";
import { useState, useEffect } from "react";
import RatingThreadDrawer from "@/modules/lead-page/new/components/ratings/rating-thread/rating-thread-drawer";
import { useRatingDetails } from "@/modules/lead-page/new/hooks/ratings/use-rating-details.hook";
import { UserV2 } from "@/modules/user/dtos/index.v2";
import {
  RatingActivityCardProps,
  RatingCommentData,
} from "@/modules/lead-page/types";

const RatingActivityCard = ({
  ratingUId,
  actorName,
  actorColor,
  rating,
  comment,
  date,
  followUpStatus,
}: RatingActivityCardProps) => {
  const theme = useTheme();
  const [isRatingThreadDrawerOpen, setIsRatingThreadDrawerOpen] =
    useState(false);

  // Fetch rating details when drawer is open
  const {
    ratingDetails,
    refetch: refetchRatingDetails,
    isLoading,
  } = useRatingDetails({
    ratingUId,
    enabled: isRatingThreadDrawerOpen, // Disabled by default, manually trigger on drawer open
  });

  // Trigger fetch when drawer opens
  useEffect(() => {
    if (isRatingThreadDrawerOpen) {
      refetchRatingDetails();
    }
  }, [isRatingThreadDrawerOpen, refetchRatingDetails]);

  return (
    <Stack
      direction="column"
      spacing={theme.nawy.spacing.xs}
      sx={{ width: "100%" }}
      display="flex"
      justifyContent="flex-start"
      alignItems="flex-start"
    >
      {/* "Review added for [User]" line */}
      <Stack
        direction="row"
        spacing={theme.nawy.spacing.xxs}
        rowGap={theme.nawy.spacing.xxs}
        flexWrap="wrap"
        alignItems="center"
        justifyContent="space-between"
        alignSelf="stretch"
      >
        <Stack
          direction="row"
          spacing={theme.nawy.spacing.xxs}
          rowGap={theme.nawy.spacing.xxs}
          flexWrap="wrap"
          alignItems="center"
        >
          <ActivityDetailsText>Review added for</ActivityDetailsText>
          {actorName && (
            <>
              <UserNameTag name={actorName} color={actorColor} />
            </>
          )}
        </Stack>

        {/* Status Badge */}
        {followUpStatus &&
          followUpStatus !== FollowUpStatusEnum.NO_FOLLOW_UP && (
            <Box sx={{ width: "70px" }}>
              <Status
                variant="secondary"
                label={capitalizeFirstLetter(followUpStatus as string)}
                color={
                  FollowUpStatusColors[followUpStatus as FollowUpStatusEnum]
                }
                textStyle={{
                  ...theme.nawy.typography.english.variants.body.xs.regular,
                }}
                width="fit-content"
              />
            </Box>
          )}
      </Stack>

      {/* Rating stars */}
      {rating !== undefined && (
        <StarRating
          rating={rating}
          size="medium"
          filledColor={nawyTheme.nawy.colors.semantic.text.accent}
        />
      )}

      {/* Comment text */}
      {comment && (
        <Typography
          sx={{
            display: "block",
            ...theme.nawy.typography.english.variants.body.md.regular,
            color: theme.nawy.colors.semantic.text.primary,
            width: "fit-content",
          }}
        >
          {comment}
        </Typography>
      )}

      {/* Date */}
      {date && (
        <Typography
          sx={{
            color: theme.nawy.colors.semantic.text.tertiary,
            ...theme.nawy.typography.english.variants.body.sm.regular,
          }}
        >
          {formatDateReadable(date)}
        </Typography>
      )}

      {/* Full review link */}
      <Link
        onClick={() => setIsRatingThreadDrawerOpen(true)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: theme.nawy.spacing.xxs,
          cursor: "pointer",
          textDecoration: "none",
          color: theme.nawy.colors.semantic.text.accent,
          ...theme.nawy.typography.english.variants.body.md.semibold,
          "&:hover": {
            textDecoration: "underline",
          },
        }}
      >
        Full review
        <ArrowRight size={16} />
      </Link>

      {/* Rating Thread Drawer */}
      <RatingThreadDrawer
        isOpen={isRatingThreadDrawerOpen}
        onClose={() => setIsRatingThreadDrawerOpen(false)}
        ratingUId={ratingUId}
        agent={ratingDetails?.agent as UserV2}
        rating={ratingDetails?.rating}
        comment={ratingDetails?.comment}
        touchpoint={ratingDetails?.status?.name}
        followUpStatus={
          ratingDetails?.followUpStatus as FollowUpStatusEnum | undefined
        }
        createdAt={ratingDetails?.createdAt}
        comments={ratingDetails?.comments as RatingCommentData[]}
        onResolvedFollowUp={refetchRatingDetails}
        isLoading={isLoading}
      />
    </Stack>
  );
};

export default RatingActivityCard;
